import React, { useState, useEffect, useRef } from 'react';
import styled, { keyframes, css } from 'styled-components';
import { Story, StoryType, AgeRange } from '../types/Story';
import { StoryCard } from './StoryCard';
import { getStories } from '../services/storyService';
import { Button } from './common/Button';
import { Select } from './common/Select';
import { useNavigate } from 'react-router-dom';
import { getLikedStoryIds } from '../services/likeService';
import { useTranslation } from 'react-i18next';
import { COLORS } from '../constants/colors';
import CustomDropdown from './common/CustomDropdown';
import { useAuth } from '../context/AuthContext';

interface StoryListProps {
  filter?: string;
  userId?: string;
  limit?: number;
  showLoadMore?: boolean;
  title?: string;
  showFilters?: boolean;
  compact?: boolean;
  sortBy?: string;
  showLike?: boolean;
  onSortChange?: (sort: string) => void;
  /* Opens "pick stories for a book" mode on mount, so the homepage
   * print section can link straight into the selection flow. */
  autoStartBookSelection?: boolean;
}

/* ─── Animations ─── */

const fadeIn = keyframes`
  from { opacity: 0; transform: translateY(8px); }
  to { opacity: 1; transform: translateY(0); }
`;

const shimmer = keyframes`
  0% { background-position: -400px 0; }
  100% { background-position: 400px 0; }
`;

const slideUp = keyframes`
  from { opacity: 0; transform: translateY(20px); }
  to { opacity: 1; transform: translateY(0); }
`;

const pulseGlow = keyframes`
  0%, 100% { box-shadow: 0 0 0 0 rgba(99, 102, 241, 0.15); }
  50% { box-shadow: 0 0 0 6px rgba(99, 102, 241, 0.05); }
`;

/* ─── Layout ─── */

const ListContainer = styled.div`
  margin-bottom: 32px;
`;

const ListHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 20px;
  flex-wrap: wrap;
  gap: 12px;
  
  @media (max-width: 768px) {
    flex-direction: column;
    align-items: stretch;
  }
`;

const Title = styled.h2`
  font-size: 1.25rem;
  font-weight: 600;
  color: #495057;
  margin: 0;
  letter-spacing: -0.01em;
`;

/* ─── Filters ─── */

const FiltersBar = styled.div`
  background: #f8f9fb;
  border: 1px solid #e9ecef;
  border-radius: 12px;
  padding: 14px 18px;
  margin-bottom: 24px;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  flex-wrap: wrap;
  animation: ${fadeIn} 0.3s ease-out;
  
  @media (max-width: 768px) {
    padding: 12px 14px;
  }
`;

const FiltersLeft = styled.div<{ $expanded: boolean }>`
  display: flex;
  gap: 10px;
  flex-wrap: wrap;
  align-items: center;
  
  @media (max-width: 768px) {
    width: 100%;
    display: ${props => props.$expanded ? 'flex' : 'none'};
    flex-direction: column;
    
    & > * {
      width: 100%;
    }
  }
`;

const FiltersRight = styled.div`
  display: flex;
  gap: 10px;
  align-items: center;
  
  @media (max-width: 768px) {
    width: 100%;
    justify-content: space-between;
  }
`;

const MobileFilterToggle = styled.button`
  display: none;
  align-items: center;
  gap: 6px;
  padding: 8px 14px;
  font-size: 0.85rem;
  font-weight: 500;
  color: #495057;
  background: white;
  border: 1px solid #dee2e6;
  border-radius: 8px;
  cursor: pointer;
  transition: all 0.2s;
  
  &:hover {
    background: #f1f3f5;
  }
  
  svg {
    width: 16px;
    height: 16px;
  }
  
  @media (max-width: 768px) {
    display: flex;
  }
`;

const FilterBadge = styled.span`
  background: ${COLORS.PRIMARY};
  color: white;
  font-size: 0.7rem;
  font-weight: 600;
  padding: 1px 6px;
  border-radius: 10px;
  margin-left: 4px;
`;

/* ─── Book Creation ─── */

const BookCreationButtons = styled.div`
  display: flex;
  gap: 8px;
  align-items: center;
`;

const BookButton = styled.button<{ $variant?: 'primary' | 'secondary' }>`
  padding: 8px 16px;
  font-size: 0.85rem;
  font-weight: 500;
  border-radius: 8px;
  border: 1px solid ${props => props.$variant === 'primary' ? COLORS.PRIMARY : '#dee2e6'};
  background-color: ${props => props.$variant === 'primary' ? COLORS.PRIMARY : 'white'};
  color: ${props => props.$variant === 'primary' ? 'white' : '#495057'};
  cursor: pointer;
  transition: all 0.2s;
  white-space: nowrap;
  
  &:hover {
    background-color: ${props => props.$variant === 'primary' ? COLORS.PRIMARY_DARK : '#f8f9fa'};
    border-color: ${props => props.$variant === 'primary' ? COLORS.PRIMARY_DARK : '#adb5bd'};
    transform: translateY(-1px);
  }
  
  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
    transform: none;
  }
`;

/* ─── Selection Mode ─── */

const SelectionBanner = styled.div`
  animation: ${slideUp} 0.3s ease-out;
  position: sticky;
  bottom: 20px;
  z-index: 50;
  margin-top: 24px;
`;

const SelectionBannerInner = styled.div<{ $variant: 'info' | 'warning' | 'success' }>`
  padding: 14px 20px;
  border-radius: 14px;
  font-size: 0.9rem;
  font-weight: 500;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  backdrop-filter: blur(12px);
  animation: ${pulseGlow} 2s ease-in-out infinite;
  
  background-color: ${props => {
    if (props.$variant === 'warning') return 'rgba(255, 243, 205, 0.95)';
    if (props.$variant === 'success') return 'rgba(209, 231, 221, 0.95)';
    return 'rgba(207, 226, 255, 0.95)';
  }};
  color: ${props => {
    if (props.$variant === 'warning') return '#997404';
    if (props.$variant === 'success') return '#0f5132';
    return '#084298';
  }};
  border: 1px solid ${props => {
    if (props.$variant === 'warning') return '#ffe69c';
    if (props.$variant === 'success') return '#badbcc';
    return '#b6d4fe';
  }};
  box-shadow: 0 8px 32px rgba(0, 0, 0, 0.1);
  
  svg {
    flex-shrink: 0;
    width: 20px;
    height: 20px;
  }
  
  @media (max-width: 576px) {
    flex-direction: column;
    text-align: center;
  }
`;

const SelectionTextGroup = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
`;

const SelectionActions = styled.div`
  display: flex;
  gap: 8px;
  flex-shrink: 0;
`;

/* ─── Selection Mode Overlay ─── */

const SelectionOverlay = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  height: 4px;
  background: linear-gradient(90deg, ${COLORS.PRIMARY}, #8b5cf6, ${COLORS.PRIMARY});
  background-size: 200% 100%;
  animation: ${shimmer} 2s linear infinite;
  z-index: 100;
`;

/* ─── Grid ─── */

const GridContainer = styled.div<{ $compact: boolean; $selectionMode: boolean }>`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(${props => props.$compact ? '240px' : '280px'}, 1fr));
  gap: ${props => props.$compact ? '16px' : '24px'};
  
  ${props => props.$selectionMode && css`
    & > * {
      transition: opacity 0.2s ease, transform 0.2s ease;
    }
  `}
`;

/* ─── Load More ─── */

const LoadMoreContainer = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  margin-top: 36px;
  gap: 10px;
`;

const StoryCount = styled.span`
  font-size: 0.825rem;
  color: #868e96;
  font-weight: 400;
`;

const ProgressBarContainer = styled.div`
  width: 200px;
  height: 3px;
  background: #e9ecef;
  border-radius: 3px;
  overflow: hidden;
`;

const ProgressBarFill = styled.div<{ $percent: number }>`
  height: 100%;
  width: ${props => props.$percent}%;
  background: ${COLORS.PRIMARY};
  border-radius: 3px;
  transition: width 0.4s ease;
`;

/* ─── Empty State ─── */

const EmptyState = styled.div`
  text-align: center;
  padding: 60px 40px;
  background: linear-gradient(135deg, #f8f9fa 0%, #f1f3f5 100%);
  border-radius: 16px;
  border: 1px dashed #dee2e6;
  animation: ${fadeIn} 0.4s ease-out;
`;

const EmptyStateIcon = styled.div`
  margin-bottom: 20px;
  
  svg {
    width: 72px;
    height: 72px;
    color: #adb5bd;
  }
`;

const EmptyStateTitle = styled.h3`
  font-size: 1.15rem;
  font-weight: 600;
  color: #495057;
  margin: 0 0 8px 0;
`;

const EmptyStateText = styled.p`
  font-size: 0.95rem;
  color: #868e96;
  margin: 0 0 20px 0;
  line-height: 1.5;
`;

/* ─── Skeleton Loading ─── */

const SkeletonCard = styled.div`
  border-radius: 12px;
  overflow: hidden;
  background: white;
  border: 1px solid #e9ecef;
`;

const SkeletonImage = styled.div`
  width: 100%;
  height: 180px;
  background: linear-gradient(90deg, #f1f3f5 0%, #e9ecef 50%, #f1f3f5 100%);
  background-size: 800px 100%;
  animation: ${shimmer} 1.5s ease-in-out infinite;
`;

const SkeletonContent = styled.div`
  padding: 16px;
`;

const SkeletonLine = styled.div<{ $width?: string; $height?: string; $mb?: string }>`
  width: ${props => props.$width || '100%'};
  height: ${props => props.$height || '14px'};
  margin-bottom: ${props => props.$mb || '10px'};
  border-radius: 6px;
  background: linear-gradient(90deg, #f1f3f5 0%, #e9ecef 50%, #f1f3f5 100%);
  background-size: 800px 100%;
  animation: ${shimmer} 1.5s ease-in-out infinite;
`;

const SkeletonGrid = styled.div<{ $compact: boolean }>`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(${props => props.$compact ? '240px' : '280px'}, 1fr));
  gap: ${props => props.$compact ? '16px' : '24px'};
`;

const SkeletonCardComponent: React.FC = () => (
  <SkeletonCard>
    <SkeletonImage />
    <SkeletonContent>
      <SkeletonLine $width="75%" $height="18px" $mb="12px" />
      <SkeletonLine $width="100%" />
      <SkeletonLine $width="60%" />
      <div style={{ display: 'flex', gap: '8px', marginTop: '14px' }}>
        <SkeletonLine $width="50px" $height="22px" $mb="0" />
        <SkeletonLine $width="65px" $height="22px" $mb="0" />
      </div>
    </SkeletonContent>
  </SkeletonCard>
);

/* ─── Component ─── */

export const StoryList: React.FC<StoryListProps> = ({
  filter,
  userId,
  limit = 10,
  showLoadMore = true,
  title,
  showFilters = false,
  compact = false,
  sortBy = 'createdAt',
  showLike = true,
  onSortChange,
  autoStartBookSelection = false
}) => {
  const [stories, setStories] = useState<Story[]>([]);
  const [totalStories, setTotalStories] = useState<number>(0);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);
  const [page, setPage] = useState<number>(1);
  const [hasMore, setHasMore] = useState<boolean>(true);
  const [sortOption, setSortOption] = useState<string>(sortBy);
  
  // Book creation state
  const [isSelectingForBook, setIsSelectingForBook] = useState<boolean>(autoStartBookSelection);
  const [selectedStoryIds, setSelectedStoryIds] = useState<Set<string>>(new Set());
  
  // Filters
  const [ageFilter, setAgeFilter] = useState<string>('all');
  const [typeFilter, setTypeFilter] = useState<string>('all');
  const [languageFilter, setLanguageFilter] = useState<string>('auto');
  
  // Mobile filter toggle
  const [filtersExpanded, setFiltersExpanded] = useState<boolean>(false);

  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const { user } = useAuth();

  const activeFilterCount = [
    ageFilter !== 'all' ? 1 : 0,
    typeFilter !== 'all' ? 1 : 0,
    languageFilter !== 'auto' ? 1 : 0,
  ].reduce((a, b) => a + b, 0);

  const fetchStories = async (currentPage: number) => {
    setLoading(true);
    setError(null);

    let filterParam = filter || '';

    if (ageFilter !== 'all') {
      filterParam += `&age=${ageFilter}`;
    }

    if (typeFilter !== 'all') {
      filterParam += `&type=${typeFilter}`;
    }

    // Resolve language filter: 'auto' uses app language, 'all' skips filter
    const resolvedLanguage = languageFilter === 'auto' ? i18n.language : languageFilter;
    if (resolvedLanguage !== 'all') {
      filterParam += `&language=${resolvedLanguage}`;
    }

    // Pass the current user's ID so the backend includes their private stories
    if (userId) {
      filterParam += `&requestingUserId=${userId}`;
    }

    try {
      const [storyRes, likedRes] = await Promise.all([
        getStories(currentPage, limit, filterParam, sortOption),
        userId ? getLikedStoryIds(userId) : Promise.resolve({ success: true, data: [] })
      ]);

      if (storyRes.success && storyRes.data) {
        const likedIds = likedRes.success && likedRes.data ? likedRes.data : [];

        const enrichedStories = storyRes.data.stories.map(story => ({
          ...story,
          liked: likedIds.includes(story.id)
        }));

        if (currentPage === 1) {
          setStories(enrichedStories);
        } else {
          setStories(prevStories => [...prevStories, ...enrichedStories]);
        }

        setTotalStories(storyRes.data.total || 0);
        setHasMore(storyRes.data.stories.length === limit);
      } else {
        setError(storyRes.error || 'Failed to load stories');
      }
    } catch (err) {
      console.log('Unexpected error: ', err);
      setError('An unexpected error occurred');
    } finally {
      setLoading(false);
    }
  };

  const sortOptions = [
    { value: 'createdAt', label: t('sorting.newest') },
    { value: 'likes', label: t('sorting.mostLiked') },
    { value: 'readCount', label: t('sorting.mostRead') }
  ];

  useEffect(() => {
    setPage(1);
    fetchStories(1);
  }, [filter, ageFilter, typeFilter, languageFilter, i18n.language, userId, sortBy, sortOption]);

  // Reset selection when filters change
  useEffect(() => {
    if (isSelectingForBook) {
      setSelectedStoryIds(new Set());
    }
  }, [ageFilter, typeFilter, languageFilter, sortOption]);

  const handleSortChange = (value: string) => {
    setSortOption(value);
    if (onSortChange) {
      onSortChange(value);
    }
  };
  
  const handleLoadMore = () => {
    const nextPage = page + 1;
    setPage(nextPage);
    fetchStories(nextPage);
  };

  const handleCreateBook = () => {
    setIsSelectingForBook(true);
    setSelectedStoryIds(new Set());
  };

  const handleCancelSelection = () => {
    setIsSelectingForBook(false);
    setSelectedStoryIds(new Set());
  };

  const handleToggleStorySelection = (storyId: string) => {
    setSelectedStoryIds(prev => {
      const newSet = new Set(prev);
      if (newSet.has(storyId)) {
        newSet.delete(storyId);
      } else {
        if (newSet.size >= 10) {
          return prev;
        }
        newSet.add(storyId);
      }
      return newSet;
    });
  };

  const handleCompleteBook = () => {
    const selectedStories = stories.filter(s => selectedStoryIds.has(s.id));
    navigate('/book/checkout', { state: { stories: selectedStories } });
    setIsSelectingForBook(false);
    setSelectedStoryIds(new Set());
  };

  const storyTypeOptions = [
    { value: 'all', label: t('storyTypes.all') },
    { value: 'adventure', label: t('storyTypes.adventure') },
    { value: 'fantasy', label: t('storyTypes.fantasy') },
    { value: 'educational', label: t('storyTypes.educational') },
    { value: 'bedtime', label: t('storyTypes.bedtime') },
    { value: 'moral', label: t('storyTypes.moral') },
    { value: 'funny', label: t('storyTypes.funny') },
    { value: 'scary', label: t('storyTypes.scary') },
    { value: 'science_fiction', label: t('storyTypes.science_fiction') },
    { value: 'historical', label: t('storyTypes.historical') },
    { value: 'animal', label: t('storyTypes.animal') },
  ];
  
  const ageRangeOptions = [
    { value: 'all', label: t('ageRanges.all') },
    { value: '2-4', label: t('ageRanges.2-4') },
    { value: '4-6', label: t('ageRanges.4-6') },
    { value: '6-9', label: t('ageRanges.6-9') },
    { value: '9-12', label: t('ageRanges.9-12') },
  ];

  const languageOptions = [
    { value: 'auto', label: t('languages.myLanguage', { defaultValue: 'My language' }) },
    { value: 'all', label: t('languages.all', { defaultValue: 'All languages' }) },
    { value: 'en', label: t('languages.en', { defaultValue: 'English' }) },
    { value: 'ro', label: t('languages.ro', { defaultValue: 'Română' }) },
    { value: 'fr', label: t('languages.fr', { defaultValue: 'Français' }) },
    { value: 'de', label: t('languages.de', { defaultValue: 'Deutsch' }) },
    { value: 'es', label: t('languages.es', { defaultValue: 'Español' }) },
    { value: 'it', label: t('languages.it', { defaultValue: 'Italiano' }) },
  ];

  const getSelectionVariant = (): 'info' | 'warning' | 'success' => {
    if (selectedStoryIds.size >= 10) return 'warning';
    if (selectedStoryIds.size >= 5) return 'success';
    return 'info';
  };

  const getSelectionMessage = (): string => {
    if (selectedStoryIds.size >= 10) {
      return t('bookSelection.messages.maximum');
    }
    if (selectedStoryIds.size >= 5) {
      return t('bookSelection.messages.progress', {
        selected: selectedStoryIds.size,
        remaining: 10 - selectedStoryIds.size
      });
    }
    return t('bookSelection.messages.minimum', {
      count: 5 - selectedStoryIds.size,
      stories: 5 - selectedStoryIds.size === 1 ? t('bookSelection.story') : t('bookSelection.stories')
    });
  };

  const getSelectionIcon = () => {
    if (selectedStoryIds.size >= 10) {
      return (
        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor">
          <path d="M10.29 3.86L1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
          <line x1="12" y1="9" x2="12" y2="13" strokeWidth="2" strokeLinecap="round"/>
          <line x1="12" y1="17" x2="12.01" y2="17" strokeWidth="2" strokeLinecap="round"/>
        </svg>
      );
    }
    if (selectedStoryIds.size >= 5) {
      return (
        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor">
          <path d="M22 11.08V12a10 10 0 1 1-5.93-9.14" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
          <polyline points="22 4 12 14.01 9 11.01" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
        </svg>
      );
    }
    return (
      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor">
        <circle cx="12" cy="12" r="10" strokeWidth="2"/>
        <line x1="12" y1="16" x2="12" y2="12" strokeWidth="2" strokeLinecap="round"/>
        <line x1="12" y1="8" x2="12.01" y2="8" strokeWidth="2" strokeLinecap="round"/>
      </svg>
    );
  };

  const displayedCount = stories.length;
  const progressPercent = totalStories > 0 ? Math.min((displayedCount / totalStories) * 100, 100) : 0;
  
  return (
    <ListContainer>
      {title && (
        <ListHeader>
          <Title>{title}</Title>
        </ListHeader>
      )}

      {showFilters && (
        <FiltersBar>
          <FiltersLeft $expanded={filtersExpanded}>
            <CustomDropdown
              options={ageRangeOptions}
              value={ageFilter}
              onChange={setAgeFilter}
            />

            <CustomDropdown
              options={storyTypeOptions}
              value={typeFilter}
              onChange={setTypeFilter}
            />

            <CustomDropdown
              options={sortOptions}
              value={sortOption}
              onChange={(value) => {
                setSortOption(value);
                if (onSortChange) {
                  onSortChange(value);
                }
              }}
            />

            <CustomDropdown
              options={languageOptions}
              value={languageFilter}
              onChange={setLanguageFilter}
            />
          </FiltersLeft>
          
          <FiltersRight>
            <MobileFilterToggle onClick={() => setFiltersExpanded(!filtersExpanded)}>
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <polygon points="22 3 2 3 10 12.46 10 19 14 21 14 12.46 22 3"/>
              </svg>
              {t('filters.toggle', { defaultValue: 'Filters' })}
              {activeFilterCount > 0 && <FilterBadge>{activeFilterCount}</FilterBadge>}
            </MobileFilterToggle>
            
            {/* {user && ( */}
              <BookCreationButtons>
                {!isSelectingForBook ? (
                  <BookButton $variant="primary" onClick={handleCreateBook}>
                    📖 {t('bookButton.create')}
                  </BookButton>
                ) : (
                  <BookButton $variant="secondary" onClick={handleCancelSelection}>
                    {t('bookButton.cancel')}
                  </BookButton>
                )}
              </BookCreationButtons>
            {/* )} */}
          </FiltersRight>
        </FiltersBar>
      )}

      {/* Selection mode top bar indicator */}
      {isSelectingForBook && <SelectionOverlay />}
      
      {loading && page === 1 ? (
        <SkeletonGrid $compact={compact}>
          {Array.from({ length: 6 }).map((_, i) => (
            <SkeletonCardComponent key={i} />
          ))}
        </SkeletonGrid>
      ) : error ? (
        <EmptyState>
          <EmptyStateIcon>
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
              <circle cx="12" cy="12" r="10" />
              <line x1="12" y1="8" x2="12" y2="12" />
              <line x1="12" y1="16" x2="12.01" y2="16" />
            </svg>
          </EmptyStateIcon>
          <EmptyStateTitle>{t('storyList.errorTitle', { defaultValue: 'Something went wrong' })}</EmptyStateTitle>
          <EmptyStateText>{error}</EmptyStateText>
          <Button variant="primary" onClick={() => fetchStories(1)}>
            {t('storyList.tryAgain')}
          </Button>
        </EmptyState>
      ) : stories.length === 0 ? (
        <EmptyState>
          <EmptyStateIcon>
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
              <path d="M4 19.5A2.5 2.5 0 0 1 6.5 17H20" />
              <path d="M6.5 2H20v20H6.5A2.5 2.5 0 0 1 4 19.5v-15A2.5 2.5 0 0 1 6.5 2z" />
              <line x1="9" y1="10" x2="15" y2="10" />
              <line x1="12" y1="7" x2="12" y2="13" />
            </svg>
          </EmptyStateIcon>
          <EmptyStateTitle>{t('storyList.noStoriesTitle', { defaultValue: 'No stories yet' })}</EmptyStateTitle>
          <EmptyStateText>{t('storyList.noStoriesFound')}</EmptyStateText>
        </EmptyState>
      ) : (
        <>
          <GridContainer $compact={compact} $selectionMode={isSelectingForBook}>
            {stories.map(story => (
              <StoryCard 
                key={story.id} 
                story={story} 
                compact={compact} 
                showLike={showLike}
                isSelectable={isSelectingForBook}
                isSelected={selectedStoryIds.has(story.id)}
                onToggleSelect={() => handleToggleStorySelection(story.id)}
                isMaxReached={selectedStoryIds.size >= 10 && !selectedStoryIds.has(story.id)}
              />
            ))}
          </GridContainer>
          
          {showLoadMore && hasMore && (
            <LoadMoreContainer>
              {totalStories > 0 && (
                <>
                  <ProgressBarContainer>
                    <ProgressBarFill $percent={progressPercent} />
                  </ProgressBarContainer>
                  <StoryCount>
                    {t('storyList.showingCount', {
                      displayed: displayedCount,
                      total: totalStories,
                      defaultValue: `Showing ${displayedCount} of ${totalStories} stories`
                    })}
                  </StoryCount>
                </>
              )}
              <Button 
                variant="outline" 
                onClick={handleLoadMore}
                isLoading={loading && page > 1}
                style={{ 
                  borderColor: COLORS.PRIMARY,
                  color: COLORS.PRIMARY
                }}
              >
                {t('storyList.loadMore')}
              </Button>
            </LoadMoreContainer>
          )}

          {/* Floating selection banner at the bottom */}
          {isSelectingForBook && (
            <SelectionBanner>
              <SelectionBannerInner $variant={getSelectionVariant()}>
                <SelectionTextGroup>
                  {getSelectionIcon()}
                  <span>{getSelectionMessage()}</span>
                </SelectionTextGroup>
                <SelectionActions>
                  <BookButton $variant="secondary" onClick={handleCancelSelection}>
                    {t('bookButton.cancel')}
                  </BookButton>
                  <BookButton
                    $variant="primary"
                    onClick={handleCompleteBook}
                    disabled={selectedStoryIds.size < 5}
                  >
                    {t('bookButton.complete')} ({selectedStoryIds.size}/10)
                  </BookButton>
                </SelectionActions>
              </SelectionBannerInner>
            </SelectionBanner>
          )}
        </>
      )}
    </ListContainer>
  );
};